// ============================================================================
// OMS sales-order drill-down — header, demand lines, and the allocation legs
// (each with its operation-stage graph). Fetches the detail endpoint on select.
// ============================================================================
import { CloudUploadOutlined, ExportOutlined, HistoryOutlined, MessageOutlined, RedoOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Descriptions, Empty, Popconfirm, Space, Spin, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import BatchConfirmModal from "../availability/components/batch_confirm_modal";
import { fmtIST } from "../format";
import { getSalesOrder, retrySalesOrder, syncPaymentToZoho } from "../oms.api";
import BatchRefundModal from "../refund/components/batch_refund_modal";
import BatchReturnDamageModal, { ReturnableLine } from "../returns/components/batch_return_modal";
import {
  ConfirmableLine,
  EstimateLine,
  OrderPaymentTransaction,
  RefundableLine,
  SalesOrderDetail as SODetail,
  SalesOrderLine,
} from "../types";
import AllocationCard from "./allocation_card";
import CopyText from "./copy_text";
import DemandLines from "./demand_lines";
import DetailSection from "./detail_section";
import { CrmIssueDrawer } from "./crm_issue_thread";
import { OrderActions } from "./escalation_actions";
import EscalationsSection, { EscalationsSummaryTag } from "./escalations_section";
import EstimateContextView from "./estimate_context";
import ExpressTag from "./express_tag";
import LogsDrawer from "./logs_drawer";
import OrderRefundsSection from "./order_refunds_section";
import SectionBoundary from "./section_boundary";
import StatusTag from "./status_tag";
import UnloadingTag from "./unloading_tag";

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

// Payment-ledger status has its own vocabulary (captured / authorized / authenticated / failed /
// refunded / credit) — distinct from the OMS fulfilment StatusTag, so it gets its own colouring.
const PAYMENT_STATUS_COLORS: Record<string, string> = {
  captured: "green",
  authorized: "green",
  authenticated: "gold",
  created: "default",
  pending: "gold",
  failed: "red",
  refunded: "red",
  credit: "blue",
};

const PaymentStatusTag: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return <Tag>—</Tag>;
  const key = status.toLowerCase();
  return <Tag color={PAYMENT_STATUS_COLORS[key] ?? "default"}>{status}</Tag>;
};

// The customer payment ledger for the order — how much was received and the state of each payment.
// Mirrors the old procurement order-details page (Transaction ID / Method / UUID / Amount / Status),
// plus the Zoho re-sync action. A factory, not a const, because the action needs the refresh callback.
const paymentColumns = (
  onSynced: (so: SODetail) => void,
  isAdmin: boolean
): ColumnsType<OrderPaymentTransaction> => [
    {
      // Zoho carries this as the payment's `reference_number` — the key the sync looks it up by, and
      // the one an operator needs when reconciling by hand in Zoho.
      title: "Payment ID",
      dataIndex: "payment_id",
      render: (v) =>
        v ? (
          <Typography.Text copyable style={{ fontSize: 12 }}>
            {v}
          </Typography.Text>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Reference ID",
      dataIndex: "reference_id",
      render: (v) => v || <span style={{ color: "#bbb" }}>—</span>,
    },
    { title: "Payment Method", dataIndex: "payment_method", render: (v) => v || "—" },
    {
      title: "UUID",
      dataIndex: "uuid",
      render: (v) =>
        v ? (
          <Typography.Text copyable style={{ fontSize: 12 }}>
            {v}
          </Typography.Text>
        ) : (
          "—"
        ),
    },
    {
      title: "Payment Amount",
      dataIndex: "payment_amount",
      align: "right",
      render: (v) => (v != null ? inr.format(Number(v)) : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <PaymentStatusTag status={v} />,
    },
    {
      title: "Date",
      dataIndex: "payment_date",
      render: (v, row) => (v ? dayjs(v).format("DD MMM YYYY") : fmtIST(row.created_at, "DD MMM YYYY")),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, row) => <SyncPaymentButton txn={row} onSynced={onSynced} disabled={!isAdmin} />,
    },
  ];

// Re-push one payment to Zoho. Only offered for the statuses the backend will actually sync
// (authorized / captured) — showing it on a row that can only ever answer "skipped" would be a
// button whose whole job is to refuse. The backend re-checks regardless; this is just not lying
// about what is on offer.
const SYNCABLE_STATUSES = ["authorized", "captured"];

const SyncPaymentButton: React.FC<{
  txn: OrderPaymentTransaction;
  onSynced: (so: SODetail) => void;
  disabled: boolean;
}> = ({ txn, onSynced, disabled }) => {
  const [syncing, setSyncing] = useState(false);
  if (!SYNCABLE_STATUSES.includes((txn.status || "").toLowerCase())) return null;

  const run = async () => {
    setSyncing(true);
    try {
      const { so } = await syncPaymentToZoho(txn.id);
      if (so) onSynced(so);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Popconfirm
      title="Sync this payment to Zoho?"
      description={
        <div style={{ maxWidth: 320 }}>
          Checks Zoho for a payment with reference{" "}
          <Typography.Text code>{txn.payment_id || "—"}</Typography.Text> and creates one only
          if it is missing. Safe to repeat. Does not re-run the order&rsquo;s payment gate.
        </div>
      }
      okText="Sync"
      onConfirm={run}
      disabled={disabled}
    >
      <Button size="small" loading={syncing} disabled={disabled} icon={<CloudUploadOutlined />}>
        Sync to Zoho
      </Button>
    </Popconfirm>
  );
};

// A line still waiting on the desk — the same rule the queue endpoint applies (a fully-cancelled
// line has nothing left to source).
const isPending = (line: SalesOrderLine) =>
  !line.availability_confirmed && Number(line.cancelled_qty) < Number(line.ordered_qty);

// The confirm dialog's minimal shape. A queue row carries its own order context; here it comes
// from the order the line belongs to.
const toConfirmable = (so: SODetail) => (line: SalesOrderLine): ConfirmableLine => ({
  id: line.id,
  sales_order_id: so.id,
  product: line.product,
  ordered_qty: line.ordered_qty,
  lead_id: so.estimate?.lead_id,
  client_name: so.estimate?.client_name,
  ship_pincode: so.estimate?.ship_address?.pincode,
});

// A line has money to refund iff any qty on it is unallotted (deallocated or never sourced). The
// refund action, the button that opens the dialog, and the row itself all key off this.
const isRefundable = (line: SalesOrderLine) => Number(line.refundable_qty) > 0;

// A line is actionable on the post-delivery side iff it has returnable OR damageable qty — the
// shared Return / Damage modal lets the operator pick which flow (the Mode toggle inside the
// modal) once it's open, so one gate opens both. The two row-level actions in LineActions share
// this same source of truth.
const isReturnable = (line: SalesOrderLine) =>
  Number(line.returnable_qty ?? 0) > 0 || Number(line.damageable_qty ?? 0) > 0;

// Pack what the Return / Damage dialog needs: product name for the row, returnable_qty +
// damageable_qty as the two mode-specific ceilings, and the estimate's unit_selling_price as the
// informational prefill rate. The rate is NOT sent to the backend on the return raise (that uses
// the authorize-refund modal later); it IS sent on damage (file_damage_cn uses the operator's
// rate directly on the CN line).
const toReturnable = (estimateLines?: EstimateLine[]) =>
  (line: SalesOrderLine): ReturnableLine => {
    const est = estimateLines?.find((e) => e.id === line.estimate_item_id);
    return {
      id: line.id,
      product_name: line.product?.product_name ?? `Line ${line.id}`,
      returnable_qty: String(line.returnable_qty ?? "0"),
      damageable_qty: String(line.damageable_qty ?? "0"),
      unit_selling_price: est?.unit_selling_price ?? "0",
      unit_price_incl_tax: est && Number(est.quantity) > 0
        ? String(((Number(est.total_price) || 0) + (Number(est.tax_value) || 0)) / Number(est.quantity))
        : "0",
      unit: line.unit,
      ordered_qty: line.ordered_qty,
      delivered_qty: line.delivered_qty,
      returned_qty: line.returned_qty,
    };
  };

// Rate comes from the estimate line the SO line was placed against — that is what the customer was
// actually charged. `estimate_item_id` is the join, kept stable across the order's life. A missing
// estimate line (rare — a repair op that unlinked it) falls back to 0 so the total shows 0 rather
// than crashing the row; the dialog then makes it visible that nothing is priced.
const toRefundable = (so: SODetail, estimateLines?: EstimateLine[]) =>
  (line: SalesOrderLine): RefundableLine => {
    const est = estimateLines?.find((e) => e.id === line.estimate_item_id);
    return {
      id: line.id,
      sales_order_id: so.id,
      product: line.product,
      refundable_qty: line.refundable_qty,
      unit_selling_price: est?.unit_selling_price ?? "0",
      unit: line.unit,
    };
  };

const SalesOrderDetailView: React.FC<{
  salesOrderId?: number;
  // Deep-link target from a board — this allocation's card opens expanded and scrolls itself
  // into view once the order has loaded. By allocation id (PO board) or, when that is all the
  // caller has, by PO number (Operations board).
  highlightAllocationId?: number;
  highlightPoNumber?: string;
}> = ({ salesOrderId, highlightAllocationId, highlightPoNumber }) => {
  // Every mutating control on this page requires oms.update; others get a read-only view.
  // "Retry failed steps" is separately gated so it can be granted on its own (oms.retry).
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission("oms.update");
  const canRetry = hasPermission("oms.retry");
  const [so, setSo] = useState<SODetail | undefined>();
  const [loading, setLoading] = useState(false);
  // Distinct from `loading` so a manual refetch spins the button without blanking
  // the whole screen back to a bare <Spin/>.
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  // The line whose availability is being confirmed, as the dialog's minimal shape — the order
  // supplies the context (enquiry, client, destination) that a queue row carries on itself.
  const [confirming, setConfirming] = useState<ConfirmableLine[]>([]);
  // Same shape mirror for the refund dialog: refundable lines the operator is about to act on.
  const [refunding, setRefunding] = useState<RefundableLine[]>([]);
  // Same shape mirror for the central Return button: returnable lines the operator is about to
  // raise returns on in one go. Empty = modal closed.
  const [returning, setReturning] = useState<ReturnableLine[]>([]);
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!salesOrderId) {
      setSo(undefined);
      return;
    }
    setLoading(true);
    getSalesOrder(salesOrderId).then((data) => {
      if (!cancelled) {
        setSo(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [salesOrderId]);

  // In-place refetch — picks up whatever the background tick / webhooks moved
  // since the order was opened, without a full page reload.
  const refresh = useCallback(async () => {
    if (!salesOrderId) return;
    setRefreshing(true);
    try {
      const data = await getSalesOrder(salesOrderId);
      if (data) setSo(data);
    } finally {
      setRefreshing(false);
    }
  }, [salesOrderId]);

  // Requeue every failed artifact on the order — including the order-level SO doc that no stage owns
  // (a failed SO blocks all legs and has no per-stage retry) — then pull the refreshed order back.
  const retry = useCallback(async () => {
    if (!salesOrderId) return;
    setRetrying(true);
    try {
      const data = await retrySalesOrder(salesOrderId);
      if (data) setSo(data);
    } finally {
      setRetrying(false);
    }
  }, [salesOrderId]);

  if (!salesOrderId) {
    return <Empty description="Select a sales order to inspect" />;
  }
  if (loading) {
    return <Spin style={{ display: "block", marginTop: 40 }} />;
  }
  if (!so) {
    return <Empty description="Could not load sales order" />;
  }

  // The PO-number deep link (Operations board) names a leg only if exactly ONE card carries that
  // number — otherwise there is nothing unambiguous to open, and highlighting several cards while
  // scrolling to the first is worse than highlighting none.
  const poMatches = highlightPoNumber
    ? so.allocations.filter((a) => a.po_number === highlightPoNumber)
    : [];
  const highlightByPo = poMatches.length === 1 ? poMatches[0].id : undefined;
  const highlightId = highlightAllocationId ?? highlightByPo;

  // sales_order_line_id -> product, so allocation item rows can name the product
  // (AllocationItem carries only the line id; the product lives on the SO line).
  const lineProducts = new Map(so.lines.map((l) => [l.id, l.product]));
  // sales_order_line_id -> unit, so the allocation ledger can show units on its qty columns too
  // (an AllocationItem carries only the line id; the unit lives on the SO line).
  const lineUnits = new Map(so.lines.map((l) => [l.id, l.unit]));

  return (
    <div>
      {/* Identity on the left, actions on the right, in a fixed order. Previously all seven
          controls sat inside the title at equal weight and re-flowed as the status changed,
          so the button under the cursor moved between visits. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Space wrap size={8}>
          {/* ENQ (the estimate's enquiry no) is the heading; the SO number rides
              alongside it, greyed out as a secondary reference. */}
          <Typography.Title level={4} style={{ margin: 0 }}>
            <CopyText value={so.estimate?.lead_id} label="Lead ID">
              {so.estimate?.lead_id || `ENQ —`}
            </CopyText>
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 14 }}>
            {so.zoho_salesorder_number || `SO #${so.id}`}
          </Typography.Text>
          <StatusTag
            status={so.status}
            tooltip={so.error_message || so.hold_reason}
            errorKind={so.error_kind}
          />
          <ExpressTag show={so.mode === "express"} />
          <UnloadingTag show={so.is_unloading} />
          <EscalationsSummaryTag escalations={so.escalations} />
        </Space>

        <Space wrap size={8}>
          {canRetry && (
            <Button
              size="small"
              danger
              icon={<RedoOutlined />}
              onClick={retry}
              loading={retrying}
            >
              Retry failed steps
            </Button>
          )}

          <OrderActions so={so} onDone={setSo} />
          {/* Read-only history, so it sits with Zoho/Refresh rather than with the actions. */}
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => setLogsOpen(true)}
          >
            Logs
          </Button>
          {so.zoho_url && (
            <Button
              size="small"
              icon={<ExportOutlined />}
              href={so.zoho_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Zoho
            </Button>
          )}
          <Button
            size="small"
            icon={<MessageOutlined />}
            onClick={() => setIssuesOpen(true)}
          >
            Escalation notes
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <CrmIssueDrawer
        salesOrderId={so.id}
        open={issuesOpen}
        onClose={() => setIssuesOpen(false)}
      />

      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Lead ID">
          <CopyText value={so.estimate?.lead_id} label="Lead ID">
            {so.estimate?.lead_id || "—"}
          </CopyText>
        </Descriptions.Item>
        <Descriptions.Item label="SO Number">
          {so.zoho_salesorder_number || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Mode">{so.mode}</Descriptions.Item>
        <Descriptions.Item label="Availability source">
          {so.availability_source}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {so.created_at ? dayjs(so.created_at).format("DD MMM YYYY HH:mm") : "—"}
        </Descriptions.Item>
        {/* Internal keys — needed when raising a ticket, but not what ops reads the page for. */}
        <Descriptions.Item label="Reference (internal)">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            estimate {so.estimate_id} · sales order {so.id}
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      {so.estimate && <EstimateContextView estimate={so.estimate} />}

      <DetailSection
        sectionKey="lines"
        title="Demand lines (fulfilment)"
        summary={
          <>
            <Tag bordered={false}>{so.lines.length} line{so.lines.length === 1 ? "" : "s"}</Tag>
            {so.pending_availability_count > 0 && (
              <Tag color="orange" bordered={false}>
                {so.pending_availability_count} awaiting availability
              </Tag>
            )}
          </>
        }
      >
      {(() => {
        // Refund is now ALWAYS surfaced on an SO. The modal loads the live Zoho SO's refundable
        // service-charge lines on open, and a charge-only refund (freight/unloading paid back
        // without touching the goods) is a legitimate flow — ops couldn't reach it before because
        // the button was gated on `so.lines.some(isRefundable)` (any unallotted goods qty) and a
        // fully-allocated SO therefore couldn't open the dialog. Lines without `refundable_qty`
        // are passed through as context but their rows render disabled, so the dialog is honest
        // about what can be acted on when no goods qty is available.
        const canRefund = true;
        const openRefund = () =>
          setRefunding(so.lines.map(toRefundable(so, so.estimate?.lines)));
        const refundBtn = (
          <Button size="small" danger onClick={openRefund}>
            Refund to customer/wallet
          </Button>
        );
        // Central Return / Damage button — one entry point for both post-delivery exits. Active
        // iff at least one line has returnable OR damageable qty; the modal's Mode toggle then
        // picks which flow the submit routes to.
        const canReturn = so.lines.some(isReturnable);
        const openReturn = () =>
          setReturning(so.lines.filter(isReturnable).map(toReturnable(so.estimate?.lines)));
        const returnBtn = (
          <Button size="small" onClick={openReturn}>
            Return / Damage
          </Button>
        );
        if (so.pending_availability_count > 0) {
          return (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${so.pending_availability_count} line${so.pending_availability_count === 1 ? "" : "s"} awaiting an availability confirmation`}
              description={
                <>
                  Nothing is allocated or procured for these lines until someone sets a lead time —
                  the order looks placed but is not moving.
                  {so.pending_availability_count > 1 && (
                    <>
                      {" "}
                      Confirm them <b>together</b>: lines sharing a source and lead time then become
                      one purchase order instead of one each.
                    </>
                  )}
                </>
              }
              action={
                isAdmin ? (
                  <Space direction="vertical" size={6} align="end">
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => setConfirming(so.lines.filter(isPending).map(toConfirmable(so)))}
                    >
                      Confirm {so.pending_availability_count > 1 ? "all" : ""}
                    </Button>
                    {canRefund && refundBtn}
                    {canReturn && returnBtn}
                  </Space>
                ) : undefined
              }
            />
          );
        }
        if (isAdmin && (canRefund || canReturn)) {
          // No pending-availability alert to hang the buttons off — surface them on their own so
          // ops can raise a refund (against unallotted qty / additional charges) or a return
          // (against delivered qty) without needing an availability-confirmation banner to be
          // present.
          return (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              {canRefund && refundBtn}
              {canReturn && returnBtn}
            </div>
          );
        }
        return null;
      })()}
      <DemandLines
        lines={so.lines}
        isAdmin={isAdmin}
        onDone={setSo}
        onConfirm={() => setConfirming(so.lines.filter(isPending).map(toConfirmable(so)))}
        estimateLines={so.estimate?.lines}
        outstandingCreditRupees={so.payment?.outstanding_credit_rupees}
        soId={so.id}
        allocations={so.allocations}
        // Row-level Return action: open the SAME batch modal, scoped to this one line, so the per-
        // line and multi-line entry points share one layout and one submit path.
        onReturnLine={(line) =>
          setReturning([toReturnable(so.estimate?.lines)(line)])
        }
        onRefundLine={(line) => setRefunding([toRefundable(so, so.estimate?.lines)(line)])}
      />
      </DetailSection>

      <BatchConfirmModal
        open={!!confirming.length}
        lines={confirming}
        onClose={() => setConfirming([])}
        onConfirmed={refresh}
      />

      <BatchRefundModal
        open={!!refunding.length}
        salesOrderId={so.id}
        lines={refunding}
        hasAuthenticatedPayment={so.payment_transactions?.some(
          (t) => (t.status || "").toLowerCase() === "authenticated"
        )}
        userId={so.estimate?.client_id}
        onClose={() => setRefunding([])}
        onRefunded={(nextSo) => setSo(nextSo)}
      />

      <BatchReturnDamageModal
        open={!!returning.length}
        salesOrderId={so.id}
        lines={returning}
        userId={so.estimate?.client_id}
        onClose={() => setReturning([])}
        onDone={refresh}
      />

      <SectionBoundary name="Customer refunds">
        <OrderRefundsSection
          salesOrderId={so.id}
          canEdit={hasPermission("refund.attach_bank")}
          reloadKey={so}
        />
      </SectionBoundary>

      {/* Money already collected — reference material once an order is moving, so it opens shut. */}
      <DetailSection
        sectionKey="payments"
        title="Payment transactions"
        defaultOpen={false}
        summary={
          <Tag bordered={false}>
            {so.payment_transactions?.length || 0} payment
            {so.payment_transactions?.length === 1 ? "" : "s"}
          </Tag>
        }
      >
        {so.payment_transactions?.length ? (
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            columns={paymentColumns(setSo, isAdmin)}
            dataSource={so.payment_transactions}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No payments recorded for this order"
          />
        )}
      </DetailSection>

      {so.escalations?.length > 0 && (
        // Opens shut once every escalation is handled: a settled mutation is history, a stuck one
        // is work, and the summary tag says which this order has without expanding anything.
        <DetailSection
          sectionKey="escalations"
          title="Escalations"
          defaultOpen={so.escalations.some((e) => e.status !== "done")}
          summary={<EscalationsSummaryTag escalations={so.escalations} />}
        >
          <SectionBoundary name="Escalations">
            <EscalationsSection escalations={so.escalations} onDone={setSo} />
          </SectionBoundary>
        </DetailSection>
      )}

      <DetailSection
        sectionKey="allocations"
        title="Allocations"
        forceOpen={highlightId !== undefined}
        summary={
          <Tag bordered={false}>
            {so.allocations.length} allotment{so.allocations.length === 1 ? "" : "s"}
          </Tag>
        }
      >
        {so.allocations.length === 0 && <Empty description="No allocations yet" />}
        {so.allocations.map((a) => (
          // One boundary PER allocation: a single unrenderable leg must not hide the other
          // allotments on the order.
          <SectionBoundary key={a.id} name={`Allocation #${a.id}`}>
            <AllocationCard
              allocation={a}
              highlight={a.id === highlightId}
              onChanged={setSo}
              lineProducts={lineProducts}
              lineUnits={lineUnits}
            />
          </SectionBoundary>
        ))}
      </DetailSection>

      <LogsDrawer
        salesOrderId={salesOrderId}
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
      />
    </div>
  );
};

export default SalesOrderDetailView;
